'use client';

import { IconLoader } from '@tabler/icons-react';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface SubscriptionLoadingOverlayProps {
    isOpen: boolean;
}

export function SubscriptionLoadingOverlay({ isOpen }: SubscriptionLoadingOverlayProps) {
    // Prevent body scroll when overlay is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Prevent escape key from closing overlay
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const overlay = (
        <div
            className="fixed inset-0 z-9999 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="loading-overlay-title"
            aria-describedby="loading-overlay-description"
        >
            <div className="flex flex-col items-center justify-center gap-6 px-4">
                {/* Loading Spinner */}
                <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-linear-to-r from-blue-500 to-purple-500 blur-xl opacity-50 animate-pulse" />
                    <div className="relative bg-neutral-900 rounded-full p-8 border border-neutral-700">
                        <IconLoader className="h-16 w-16 text-blue-400 animate-spin-slow" aria-hidden="true" />
                    </div>
                </div>

                {/* Messages */}
                <div className="text-center space-y-3 max-w-md">
                    <h2 id="loading-overlay-title" className="text-2xl font-semibold text-white">
                        Creating your subscription...
                    </h2>
                    <p id="loading-overlay-description" className="text-neutral-300 text-lg">
                        Please don&apos;t close this window
                    </p>
                    <p className="text-neutral-400 text-sm">This may take a few moments as we set up your account</p>
                </div>

                {/* Accessibility: Screen reader live region */}
                <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
                    Creating your subscription. Please wait and do not close this window.
                </div>
            </div>
        </div>
    );

    // Use portal to render at document body level
    if (typeof document !== 'undefined') {
        return createPortal(overlay, document.body);
    }

    return overlay;
}
